'use client';

import { useState, useEffect } from 'react';
import Breadcrumb from '@/components/ui/Breadcrumb';
import NavBar from '../home-page/components/NavBar';
import FooterSection from '../home-page/components/FooterSection';
import { generateFAQSchema, generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { faqService, type FAQ, type FAQsByCategory } from '@/lib/services/faqService';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';

export default function FAQPage() {
  const [faqsByCategory, setFaqsByCategory] = useState<FAQsByCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCategoryIndex, setOpenCategoryIndex] = useState<number | null>(0);
  const [openFaqIndex, setOpenFaqIndex] = useState<{ [key: string]: number | null }>({});

  useEffect(() => {
    const loadFAQs = async () => {
      try {
        const data = await faqService.getActiveGroupedByCategory();
        setFaqsByCategory(data);
      } catch (error) {
        console.error('Failed to load FAQs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFAQs();
  }, []);

  const toggleCategory = (index: number) => {
    setOpenCategoryIndex(openCategoryIndex === index ? null : index);
  };

  const toggleFAQ = (categoryIndex: number, faqIndex: number) => {
    const key = `${categoryIndex}`;
    setOpenFaqIndex({
      ...openFaqIndex,
      [key]: openFaqIndex[key] === faqIndex ? null : faqIndex,
    });
  };

  // Flatten all FAQs for schema
  const allFaqs = faqsByCategory.flatMap(group => group.faqs);
  
  const faqSchema = generateFAQSchema(
    allFaqs.map((faq) => ({ question: faq.question, answer: faq.answer }))
  );
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: `${SITE_URL}/home-page` },
    { name: 'FAQ', url: `${SITE_URL}/faq` },
  ]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <Breadcrumb items={[{ label: 'FAQ' }]} />
            </div>
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h1 className="text-5xl font-bold text-navy-dark mb-6">Frequently Asked Questions</h1>
                <p className="text-xl text-body-text">
                  Find answers to common questions about GoGedit, organized by topic
                </p>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-body-text">Loading FAQs...</p>
                  </div>
                </div>
              ) : faqsByCategory.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No FAQs available at the moment.
                </div>
              ) : (
                <div className="space-y-6">
                  {faqsByCategory.map((categoryGroup, categoryIndex) => (
                    <div
                      key={categoryGroup.category}
                      className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-md"
                    >
                      {/* Category Header */}
                      <button
                        onClick={() => toggleCategory(categoryIndex)}
                        className="w-full px-6 py-5 text-left flex justify-between items-center bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                            <span className="text-white font-bold text-lg">
                              {categoryGroup.faqs.length}
                            </span>
                          </div>
                          <div>
                            <h2 className="text-xl font-bold text-navy-dark">
                              {categoryGroup.category}
                            </h2>
                            <p className="text-sm text-gray-600">
                              {categoryGroup.faqs.length} {categoryGroup.faqs.length === 1 ? 'question' : 'questions'}
                            </p>
                          </div>
                        </div>
                        <svg
                          className={`w-7 h-7 text-indigo-600 flex-shrink-0 transition-transform ${
                            openCategoryIndex === categoryIndex ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Category FAQs */}
                      {openCategoryIndex === categoryIndex && (
                        <div className="border-t border-gray-200">
                          {categoryGroup.faqs.map((faq, faqIndex) => (
                            <div
                              key={faq.id}
                              className="border-b border-gray-100 last:border-b-0"
                            >
                              <button
                                onClick={() => toggleFAQ(categoryIndex, faqIndex)}
                                className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                              >
                                <span className="text-base font-semibold text-navy-dark pr-4">
                                  {faq.question}
                                </span>
                                <svg
                                  className={`w-5 h-5 text-brand-purple flex-shrink-0 transition-transform ${
                                    openFaqIndex[`${categoryIndex}`] === faqIndex ? 'rotate-180' : ''
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {openFaqIndex[`${categoryIndex}`] === faqIndex && (
                                <div className="px-6 pb-4 bg-gray-50">
                                  <p className="text-body-text leading-relaxed">{faq.answer}</p>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-16 bg-purple-light rounded-2xl p-8 text-center">
                <h2 className="text-2xl font-bold text-navy-dark mb-4">Still have questions?</h2>
                <p className="text-body-text mb-6">
                  Can't find the answer you're looking for? Our support team is here to help.
                </p>
                <a
                  href="/contact-us"
                  className="inline-block bg-brand-purple text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>

        <FooterSection />
      </div>
    </>
  );
}